import LRUCache from "ol/structs/LRUCache.js";
import * as three from "three";
import type { TileList } from "../layers/TileLayer";
import { constant } from "../constant";

/**
 * A Tile
 */
export interface Tile {
  /**
   * Whether the tile is ready or not
   */
  ready: boolean; // TODO ready==true means texture is loaded? if so, remove ready flag and use only nullable texture field
  /**
   * A three texture. If the texture is ready (loaded) then it is non null
   */
  texture: three.Texture | null;
  /**
   * The tile id in the form `"z/x/y"`, for example `"0/0/0"`
   */
  zxy: string;
}

/**
 * An instance of TileTextureSource manages textures (loading, freeing, caching) with tile ID approach
 */
class TileTextureSource {
  private loader = new three.TextureLoader();
  private lru = new LRUCache<Tile>(32);
  private apiKey = "";
  private sessionId = "";

  /**
   *
   * @param url The URL must have a pattern such as `https://api.maptiler.com/tiles/something/{zxy}.png`
   * @param onTileLoad
   */
  constructor(
    private readonly url: string,
    private readonly onTileLoad?: (tile: Tile, url?: string, error?: ErrorEvent | null) => void,
  ) {}

  setMaptilerParams(apiKey: string, sessionId: string) {
    this.apiKey = apiKey;
    this.sessionId = sessionId;
  }

  private urlPatternToUrl(zxy: string): string {
    let urlObj: URL;

    // if the URL fails to build then it means it was provided as a local URL
    try {
      urlObj = new URL(this.url.replace("{zxy}", zxy));
    } catch (e) {
      urlObj = new URL(this.url.replace("{zxy}", zxy), document.baseURI);
    }

    if (urlObj.host === constant.maptilerApiHost) {
      if (this.apiKey && !urlObj.searchParams.has("key")) {
        urlObj.searchParams.append("key", this.apiKey);
      }

      if (this.sessionId && !urlObj.searchParams.has("mtsid")) {
        urlObj.searchParams.append("mtsid", this.sessionId);
      }
    }

    return urlObj.href;
  }

  /**
   * Get a tile from its ID.
   * Retrieves the tile from cache if present, otherwise loads it and adds it to cache
   * @param zxy of shape `"z/x/y"`, for example `"0/0/0"`
   * @param load
   * @returns
   */
  getTile(zxy: string, load: boolean): Tile | null {
    const hasNegatives = zxy
      .split("/")
      .map((el) => Number.parseInt(el))
      .some((el) => el < 0);
    if (hasNegatives) return null;

    if (this.lru.containsKey(zxy)) {
      return this.lru.get(zxy);
    }

    if (load) {
      const tile = this.createTile(zxy);
      this.lru.set(zxy, tile);
      return tile;
    }

    return null;
  }

  /**
   * Loads a tile and adds it to cache. When created `.ready` is `false` but when the loading callback
   * is successful, it is turned to `true`. Due to this asynchronous behaviour, the `Tile`returned by
   * this function will always have its `.ready`attribute to false.
   *
   * @param zxy of shape `"z/x/y"`, for example `"0/0/0"`
   * @returns
   */
  private createTile(zxy: string): Tile {
    const url = this.urlPatternToUrl(zxy);
    const tile: Tile = {
      ready: false,
      texture: null,
      zxy: zxy,
    };

    tile.texture = this.loader.load(
      url,
      () => {
        tile.ready = true;

        if (this.onTileLoad != null) {
          this.onTileLoad(tile, url, null);
        }
      },

      // on progress callback (deprecated)
      undefined,

      // On load fail callback
      (error) => {
        if (this.onTileLoad != null) {
          this.onTileLoad(tile, url, error as ErrorEvent);
        }
      },
    );

    tile.texture.minFilter = three.LinearFilter;
    tile.texture.needsUpdate = true;

    return tile;
  }

  /**
   * Frees the GPU related resources allocated by a texture. See [Texture.dispose() on ThreeJS](https://threejs.org/docs/?q=texture#api/en/textures/Texture.dispose).
   * @param tile
   */
  private disposeTile(tile: Tile) {
    if (tile.texture) {
      tile.texture.dispose();
    }
  }

  /**
   * Dispose all the Tiles that are overflowing the size of the cache, except the ones provided in argument.
   * @param usedTiles
   */
  expireCache(usedTiles: TileList) {
    while (this.lru.canExpireCache()) {
      const tile = this.lru.peekLast();
      if (tile.zxy in usedTiles) {
        break;
      }

      this.disposeTile(this.lru.pop());
    }
  }

  /**
   * Dispose all the tiles from the cache (clearing the cache)
   */
  dispose() {
    for (const tile of this.lru.getValues()) {
      this.disposeTile(tile);
    }
    this.lru.clear();
  }
}

export { TileTextureSource };
